import { defineAsyncComponent } from "vue";
import { PAGE_TYPE, ROLE_TYPE } from "@supplier/meta";
import contractRouters from "@/routers/warehouseSupervision/contractManagement";
import storageRouter from "@/routers/warehouseSupervision/storage";
import { UserStore, useUserStore } from "@/stores/user";
import { hasWarehouseSupervision } from "@/utils/permission";
import { AccountStore, AccountType } from "@/stores/account";

export const warehouseSupervisionRouters = [
    {
        path: "/warehouseSupervision",
        name: "WarehouseSupervision",
        component: () => import("@/views/layouts/index.vue"),
        redirect: "/warehouseSupervision/warehouseContract",
        meta: {
            title: "仓储业务",
            icon: "icon-a-Frame52",
            auth: ({ userStore, accountStore }: { userStore: UserStore; accountStore: AccountStore }) => {
                let roles = userStore.userInfo.proxyAccount?.roles;
                // 关联企业是跟随主账号的角色权限
                if (accountStore.accountType === AccountType.ASSOCIATED) {
                    roles = userStore.userInfo.customer?.roles;
                }
                return (
                    !!roles?.includes(ROLE_TYPE.WAREHOUSE_CUSTOMER.code) &&
                    (!accountStore.isSubAccount || hasWarehouseSupervision(userStore.userInfo.permissions))
                );
            }
        },
        children: [
            ...contractRouters,
            ...storageRouter,
            {
                path: "/warehouseStorage/stockTake",
                name: "WarehouseStockTake",
                component: defineAsyncComponent(() => {
                    const userStore = useUserStore();
                    if (userStore?.isSAAS) {
                        return import("@/components/emptyPage/index.vue");
                    } else {
                        return import("@/views/pages/warehouseSupervision/storageManagement/stocktakeManagement/index.vue");
                    }
                }),
                meta: {
                    title: "盘点管理",
                    activeMenu: "WarehouseStockTake",
                    pageType: PAGE_TYPE.LIST.code
                }
            },
            {
                path: "/warehouseStorage/stockTake/detail",
                name: "WarehouseStockTakeDetail",
                component: () => import("@/views/pages/warehouseSupervision/storageManagement/stocktakeManagement/detail.vue"),
                meta: {
                    title: "盘点详情",
                    activeMenu: "WarehouseStockTake",
                    isNotShowHeader: true,
                    hidden: true,
                    pageType: PAGE_TYPE.DETAIL.code
                }
            },
            {
                path: "/warehouseSupervision/inventoryRandomInspection",
                name: "WarehouseInventoryRandomInspection",
                component: defineAsyncComponent(() => {
                    const userStore = useUserStore();
                    if (userStore?.isSAAS) {
                        return import("@/components/emptyPage/index.vue");
                    } else {
                        return import("@/views/pages/warehouseSupervision/inventoryRandomInspection/index.vue");
                    }
                }),
                meta: {
                    title: "库存抽检",
                    pageType: PAGE_TYPE.LIST.code
                }
            },
            {
                path: "/warehouseSupervision/inventoryRandomInspection/detail/:id",
                name: "WarehouseInventoryRandomInspectionDetail",
                component: () => import("@/views/pages/warehouseSupervision/inventoryRandomInspection/detail.vue"),
                meta: {
                    title: "抽检详情",
                    activeMenu: "WarehouseInventoryRandomInspection",
                    hidden: true,
                    isNotShowHeader: true,
                    pageType: PAGE_TYPE.DETAIL.code
                }
            },
            {
                path: "storagePledge",
                name: "StoragePledgeManagement",
                component: defineAsyncComponent(() => {
                    const userStore = useUserStore();
                    if (userStore?.isSAAS) {
                        return import("@/components/emptyPage/index.vue");
                    } else {
                        return import("@/views/pages/warehouseSupervision/storageManagement/pledgeManagement/index.vue");
                    }
                }),
                meta: {
                    title: "质押记录",
                    pageType: PAGE_TYPE.LIST.code
                }
            },
            {
                path: "storagePledge/detail/:id",
                name: "StoragePledgeDetail",
                component: () => import("@/views/pages/warehouseSupervision/storageManagement/pledgeManagement/detail.vue"),
                meta: {
                    title: "质押详情",
                    activeMenu: "StoragePledgeManagement",
                    hidden: true,
                    isNotShowHeader: true,
                    pageType: PAGE_TYPE.DETAIL.code
                }
            },
            {
                path: "transfer",
                name: "TransferManagement",
                component: defineAsyncComponent(() => {
                    const userStore = useUserStore();
                    if (userStore?.isSAAS) {
                        return import("@/components/emptyPage/index.vue");
                    } else {
                        return import("@/views/pages/storageManagement/transferManagement/index.vue");
                    }
                }),
                meta: {
                    title: "过户管理",
                    pageType: PAGE_TYPE.LIST.code
                }
            },
            {
                path: "transfer/detail/:id",
                name: "TransferDetail",
                component: () => import("@/views/pages/storageManagement/transferManagement/details.vue"),
                meta: {
                    title: "过户详情",
                    activeMenu: "TransferManagement",
                    hidden: true,
                    isNotShowHeader: true,
                    pageType: PAGE_TYPE.DETAIL.code
                }
            },
            {
                path: "transfer/add",
                name: "TransferAdd",
                component: () => import("@/views/pages/storageManagement/transferManagement/add.vue"),
                meta: {
                    title: "新增过户",
                    activeMenu: "TransferManagement",
                    hidden: true,
                    isNotShowHeader: true,
                    pageType: PAGE_TYPE.CACHE_FORM.code,
                    keepAlive: true
                }
            },
            {
                path: "transfer/edit/:id",
                name: "TransferEdit",
                component: () => import("@/views/pages/storageManagement/transferManagement/add.vue"),
                meta: {
                    title: "编辑过户",
                    activeMenu: "TransferManagement",
                    hidden: true,
                    isNotShowHeader: true,
                    pageType: PAGE_TYPE.CACHE_FORM.code,
                    keepAlive: true
                }
            },
            {
                path: "/WarehouseStatementStatistics/stock",
                name: "WarehouseStockStatement",
                meta: {
                    title: "库存报表"
                },
                children: [
                    {
                        path: "detailed",
                        name: "WarehouseStockDetailed",
                        component: defineAsyncComponent(() => {
                            const userStore = useUserStore();
                            if (userStore?.isSAAS) {
                                return import("@/components/emptyPage/index.vue");
                            } else {
                                return import("@/views/pages/warehouseSupervision/storageManagement/stockStatement/detailed/index.vue");
                            }
                        }),
                        meta: {
                            title: "库存明细",
                            isNotShowHeader: true,
                            keepAlive: true,
                            pageType: PAGE_TYPE.LIST.code
                        }
                    },
                    {
                        path: "summary",
                        name: "WarehouseStockSummary",
                        component: defineAsyncComponent(() => {
                            const userStore = useUserStore();
                            if (userStore?.isSAAS) {
                                return import("@/components/emptyPage/index.vue");
                            } else {
                                return import("@/views/pages/warehouseSupervision/storageManagement/stockStatement/summary/index.vue");
                            }
                        }),
                        meta: {
                            title: "库存汇总",
                            pageType: PAGE_TYPE.LIST.code,
                            isNotShowHeader: true,
                            keepAlive: true
                        }
                    }
                ]
            }
        ]
    }
];
